import React, { useState, useEffect } from "react";
import ReactTable from "react-table-v6";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import commaNumber from "comma-number";
import Loader from "../../../../../../settings/loader";
import { useAppContext } from "../../../../../../../AppContext";
import "react-table-v6/react-table.css";
import "../style.scss";

function Slide1(props) {
  const { user, chimeraserv } = useAppContext();
  const [data, setData] = useState({
    outletTargets: [],
  });
  const [load, setLoad] = useState(0);
  const [editingTarget, setEditingTarget] = useState(null);
  const [tempTargetValue, setTempTargetValue] = useState("");
  const [savingTarget, setSavingTarget] = useState(null);

  useEffect(() => {
    // Add comprehensive null checks
    if (props && props.data && props.data.state && !load) {
      // Safe access to brands with multiple fallbacks
      const brands = props.data?.result?.brands || [];

      // Process brands and their chains (outlets)
      const outletTargets = [];

      if (Array.isArray(brands) && brands.length > 0) {
        brands.forEach((brand, brandIndex) => {
          // Safe access to chains
          const chains = brand?.chains || [];

          if (Array.isArray(chains) && chains.length > 0) {
            chains.forEach((chain, chainIndex) => {
              outletTargets.push({
                id: `${brand?.id || brand?.name || brandIndex}_${chainIndex}`,
                brandName: brand?.name || `Brand ${brandIndex + 1}`,
                brandId: brand?.id || brand?._id,
                brandKey: brand?.braKey || brand?.id, // Add brandKey for API
                outletName: chain?.name || `Outlet ${chainIndex + 1}`,
                chainKey: chain?.chaKey || chain?.id, // Add chainKey for API
                target: Number(chain?.target) || 0,
                originalTarget: Number(chain?.target) || 0,
                chainIndex: chainIndex,
                brandIndex: brandIndex,
              });
            });
          } else {
          }
        });
      } else {
        console.log("No brands found");
      }

      setData({
        outletTargets: outletTargets,
      });
      setLoad(1);
    }
  }, [props.data, load]);

  // Handle target editing
  const handleEditTarget = (rowId, currentTarget) => {
    setEditingTarget(rowId);
    setTempTargetValue(currentTarget.toString());
  };

  // Updated handleSaveTarget with API call
  const handleSaveTarget = async (rowId) => {
    const outlet = data.outletTargets.find((o) => o.id === rowId);
    const newTarget = parseFloat(tempTargetValue) || 0;

    if (!outlet) {
      return;
    }

    if (!outlet.chainKey || !outlet.brandKey) {
      console.error("Missing chainKey or brandKey:", outlet);
      alert("Error: Missing chain or brand key. Cannot save target.");
      return;
    }

    setSavingTarget(rowId);

    try {
      const headers = {
        Authorization: user.data.usrKey,
        "Content-Type": "application/json",
        Accept: "application/json",
      };

      const requestBody = {
        target: newTarget,
        grpId: user.data.group,
        braKey: outlet.brandKey,
      };

      const response = await fetch(
        `${chimeraserv.urlPost}/chain/${outlet.chainKey}/target`,
        {
          method: "PUT",
          headers: new Headers(headers),
          body: JSON.stringify(requestBody),
        }
      );

      const result = await response.json();

      if (response.ok && result.success !== false) {
        // Update local state on successful API call
        const updatedData = data.outletTargets.map((outletItem) => {
          if (outletItem.id === rowId) {
            return {
              ...outletItem,
              target: newTarget,
              originalTarget: newTarget,
            };
          }
          return outletItem;
        });

        setData({
          outletTargets: updatedData,
        });

        setEditingTarget(null);
        setTempTargetValue("");

        // Show success message

        // Optional: Show toast notification
        // showNotification("Target updated successfully", "success");
      } else {
        alert(`Error updating target: ${result.message || "Unknown error"}`);
      }
    } catch (error) {
      alert(`Network error: ${error.message}`);
    } finally {
      setSavingTarget(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingTarget(null);
    setTempTargetValue("");
  };

  const handleTargetChange = (e) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setTempTargetValue(value);
    }
  };

  // Export logic (existing code remains the same)
  useEffect(() => {
    if (props.export && data.outletTargets.length) {
      const exportData = data.outletTargets.map((outlet) => [
        outlet.brandName,
        outlet.outletName,
        commaNumber(outlet.target),
      ]);

      const brandSummary = {};
      data.outletTargets.forEach((outlet) => {
        if (!brandSummary[outlet.brandName]) {
          brandSummary[outlet.brandName] = {
            outletCount: 0,
            totalTarget: 0,
          };
        }
        brandSummary[outlet.brandName].outletCount++;
        brandSummary[outlet.brandName].totalTarget += outlet.target;
      });

      const summaryData = Object.entries(brandSummary).map(
        ([brandName, data]) => [
          brandName,
          data.outletCount,
          commaNumber(data.totalTarget),
          commaNumber(Math.round(data.totalTarget / data.outletCount)),
        ]
      );

      props.getData({
        type: "table",
        title: "Outlet Targets by Brand",
        subtitle: `Target management across ${data.outletTargets.length} outlets`,
        body: [
          {
            title: "Brand Summary",
            head: [["Brand", "Outlets", "Total Target", "Avg Target"]],
            body: summaryData,
          },
          {
            title: "Detailed Outlet Targets",
            head: [["Brand", "Outlet", "Target"]],
            body: exportData,
          },
        ],
        summary: [
          {
            title: "Overview",
            data: [
              { label: "Total Outlets", value: data.outletTargets.length },
              {
                label: "Total Brands",
                value: Object.keys(brandSummary).length,
              },
              {
                label: "Combined Target",
                value: commaNumber(
                  data.outletTargets.reduce(
                    (sum, outlet) => sum + outlet.target,
                    0
                  )
                ),
              },
              {
                label: "Average Target",
                value: commaNumber(
                  Math.round(
                    data.outletTargets.reduce(
                      (sum, outlet) => sum + outlet.target,
                      0
                    ) / data.outletTargets.length
                  )
                ),
              },
            ],
          },
        ],
      });
    }
    // eslint-disable-next-line
  }, [props.export, data.outletTargets]);

  const columns = [
    {
      Header: "Brand",
      accessor: "brandName",
      Cell: (row) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontWeight: "600", color: "#333" }}>
            {row.value || "Unknown Brand"}
          </span>
        </div>
      ),
      width: 200,
    },
    {
      Header: "Outlet",
      accessor: "outletName",
      Cell: (row) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ color: "#333" }}>{row.value || "Unknown Outlet"}</span>
        </div>
      ),
      width: 250,
    },
    {
      Header: "Target",
      accessor: "target",
      Cell: (row) => {
        const isEditing = editingTarget === row.original.id;
        const isSaving = savingTarget === row.original.id;

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {isEditing ? (
              <div
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="text"
                  value={tempTargetValue}
                  onChange={handleTargetChange}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleSaveTarget(row.original.id);
                    } else if (e.key === "Escape") {
                      handleCancelEdit();
                    }
                  }}
                  style={{
                    width: "80px",
                    padding: "4px 8px",
                    border: "1px solid #e08207ff",
                    borderRadius: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                  }}
                  autoFocus
                  disabled={isSaving}
                />
                <button
                  onClick={() => handleSaveTarget(row.original.id)}
                  disabled={isSaving}
                  style={{
                    background: isSaving ? "#6c757d" : "#e08207ff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    padding: "4px 6px",
                    cursor: isSaving ? "not-allowed" : "pointer",
                    fontSize: "12px",
                  }}
                  title={isSaving ? "Saving..." : "Save"}
                >
                  {isSaving ? "Save" : "Save"}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  style={{
                    background: "#6f6f6fff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    padding: "4px 6px",
                    cursor: isSaving ? "not-allowed" : "pointer",
                    fontSize: "12px",
                  }}
                  title="Cancel"
                >
                  <FontAwesomeIcon icon={["fas", "times"]} />
                </button>
              </div>
            ) : (
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span
                  style={{
                    // fontWeight: "bold",
                    color: row.value > 0 ? "#171717c7" : "#6c757d",
                    fontSize: "16px",
                    minWidth: "60px",
                    textAlign: "center",
                  }}
                >
                  {commaNumber(row.value || 0)}
                </span>
                <button
                  onClick={() => handleEditTarget(row.original.id, row.value)}
                  disabled={savingTarget !== null}
                  style={{
                    background: "#e08207ff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    padding: "4px 6px",
                    cursor: savingTarget !== null ? "not-allowed" : "pointer",
                    fontSize: "12px",
                    opacity: savingTarget !== null ? 0.6 : 1,
                  }}
                  title="Edit Target"
                >
                  <FontAwesomeIcon icon={["fas", "edit"]} />
                </button>
              </div>
            )}
          </div>
        );
      },
      width: 180,
    },
  ];

  if (!load) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h3>Processing outlet data...</h3>
        <Loader />
      </div>
    );
  }

  return (
    <div className="outlet-targets-slide slide1">
      <div style={{ padding: "20px" }}>
        {data.outletTargets.length > 0 ? (
          <div className="table-container">
            <ReactTable
              className="-striped -highlight outlet-targets-table"
              data={data.outletTargets}
              columns={columns}
              defaultPageSize={10}
              sortable
              filterable
              defaultSorted={[
                {
                  id: "brandName",
                  desc: false,
                },
              ]}
              getTdProps={() => ({
                style: {
                  textAlign: "center",
                  padding: "12px",
                  verticalAlign: "middle",
                },
              })}
              noDataText="No outlet data available"
            />
          </div>
        ) : (
          <div
            style={{
              padding: "40px",
              background: "#f8f9fa",
              borderRadius: "8px",
              textAlign: "center",
              margin: "20px 0",
            }}
          >
            <FontAwesomeIcon
              icon={["fas", "info-circle"]}
              style={{
                fontSize: "48px",
                color: "#6c757d",
                marginBottom: "20px",
              }}
            />
            <h4>No outlet data found</h4>
            <p>Please check your API configuration or add some outlets.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Slide1;
